import {
  getProvisionalIdentifierForType,
  isNumeric,
  isArray,
  normalizeIdentifier,
} from '../util';
import { createError } from '../error';

class FieldResponse {
  constructor(data) {
    "ngInject";
    data = data || {};

    this.user_role_id = data.user_role_id || null;
    this.current_location = data.current_location || null;

    this.id = data.id || getProvisionalIdentifierForType('fieldresponse');
    this.value = data.value !== null ? data.value : null;
    this.original_values = data.original_values && data.original_values.length ? data.original_values : null;
    this.has_submitted_value_changed = data.has_submitted_value_changed || false;

    this.events_id = data.events_id || null;
    this.events_custom_responses_id = data.events_custom_responses_id || null;
    this.formResponse = data.formResponse || null;
    this.events_custom_forms_responses_id = this.formResponse && this.formResponse.id
      || data.events_custom_forms_responses_id
      || null;

    this.fieldInstance = data.fieldInstance || null;
    if (!this.fieldInstance) {
      throw createError({
        name: "NoFieldInstanceProvided",
        message: "Field instance required for FieldResponse",
      });
    }
    this.fieldBlock = this.fieldInstance.fieldBlock || null;

    this.lastVisibleCheck = null;
    this.events_custom_instances_id = this.fieldInstance.id;
    this.events_custom_id = this.fieldInstance.field.id;
    this.recapResponse = data.recapResponse || null;
    this.eventTimezoneName = data.eventTimezoneName
        || (data.recapResponse && data.recapResponse.eventTimezoneName)
        || null;

    this._documentsToUpload = [];
    this._documentsToDelete = [];

    this._errors = [];
    this._visible = false;
    this._visibleStatusDirty = true;
  }

  debug() {
    if (!this._debugPrefix) {
      this._debugPrefix = `FieldResponse[id=${this.id}].debug()`;
    }
    const args = [this._debugPrefix, ...arguments];
    if (console) {
      if (console.debug) {
        console.debug.apply(null, args);
      } else {
        console.log.apply(null, args);
      }
    }
  }

  get value() {
    return this._value;
  }

  set value(value) {
    this._value = value;
    this._currentValue = value;
    this.setLogicStatusDirty();
  }

  get currentValue() {
    return this._currentValue;
  }

  set currentValue(currentValue) {
    this._currentValue = currentValue;
    this.setLogicStatusDirty();
  }

  get numericValue() {
    return this.numericValueFromValue(this.value);
  }

  get currentNumericValue() {
    return this.numericValueFromValue(this.currentValue);
  }

  setLogicStatusDirty() {
    this._logicStatusDirty = true;
  }

  /**
   * Sets logic status dirty only if one of the specified field responses may impact us via logic condition dependency
   */
  setLogicStatusDirtyIfAffected(changedFieldResponses) {
    if (this._logicStatusDirty) {
      // Don't bother to check if already dirty
      return;
    }
    if (changedFieldResponses) {
      this._logicStatusDirty = changedFieldResponses.some(fieldResponse => this._isLogicAffectedByFieldInstance(fieldResponse.fieldInstance));
    } else {
      this._logicStatusDirty = true;
    }
  }

  /**
   * Determine if my display logic is affected by the provided fieldResponse:
   *  - If the fieldResponse is me, then I'm affected!
   *  - If I have logic conditions,
   *    - AND those logic conditions reference other fields (directly or indirectly),
   *      - AND any of those referenced fields match the specified fieldResponse,
   *        - then I'm affected
   *  - Otherwise I'm not affected
   */
  _isLogicAffectedByFieldInstance(fieldInstance) {
    if (!fieldInstance || !fieldInstance.id) {
      console.warn(`Error! Should have gotten fieldInstance, but got:`, fieldInstance);
    }
    // Is my logic affected by the specified field response?
    const allDependencies = this.allDependencyFieldInstanceIdsMap;
    return allDependencies.has(fieldInstance.id);
  }

  /**
   * Build a Map object of (id => true) key/value pairs for all dependency field instances
   */
  get allDependencyFieldInstanceIdsMap() {
    if (!this._allDependencyFieldInstanceIdsMap) {
      const fieldInstances = this._findAllDependentFieldInstances();
      this._allDependencyFieldInstanceIdsMap = fieldInstances.reduce((map, fieldInstance) => {
        map.set(fieldInstance.id, true);
        return map;
      }, new Map());
    }
    return this._allDependencyFieldInstanceIdsMap;
  }

  _findAllDependentFieldInstances() {
    if (!this.fieldInstance) {
      return [];
    }
    return this.fieldInstance.getAllReferencedFieldInstances();
  }

  numericValueFromValue(value) {
    let numericValue = null;
    const field = this.fieldInstance && this.fieldInstance.field;
    if (!field) {
      return null;
    }
    if (field.isSelectWithNumericEquivalent) {
      const selectedOption = this.fieldOptionWithId(value);
      if (selectedOption) {
        numericValue = selectedOption.numeric_equivalent;
      }
    } else if (field.hasOptions()) {
      // Ignore other fields with options
    } else {
      numericValue = isNumeric(value) ? value : null;
    }
    return numericValue;
  }

  fieldOptionWithId(id) {
    const normalizedId = normalizeIdentifier(id);
    if (!id) {
      return null;
    }
    const options = this.fieldOptions;
    if (!isArray(options)) {
      return null;
    }
    return options.find(o => normalizeIdentifier(o.id) === normalizedId);
  }

  get required() {
    // Read-only fields are never technically "required" (MOSAICPT-1882)
    if (!this.fieldType || this.fieldType.isReadOnly) {
      return false;
    }
    return this.fieldInstance.validations.some(validation => {
      return validation.type.required
    });
  }

  get fieldType() {
    return this.fieldInstance && this.fieldInstance.field && this.fieldInstance.field.fieldType;
  }

  get fieldOptions() {
    return this.fieldInstance
      && this.fieldInstance.field
      && this.fieldInstance.field.options;
  }

  get isEmpty() {
    let currentValueEmpty = this.currentValue === undefined
      || this.currentValue === null
      || this.currentValue === '';

    // If numeric value, treat NaN as empty
    if (typeof this.currentValue === "number") {
      currentValueEmpty = currentValueEmpty || isNaN(this.currentValue);
    }

    let pendingDocsEmpty = this.documentsToUpload.length == 0;
    return currentValueEmpty && pendingDocsEmpty;
  }

  set errors(errors) {
    this._errors = errors;
  }

  get errors() {
    return this._errors;
  }

  get hasErrors() {
    return this.validate().length > 0;
  }

  get visible() {
    if (this._logicStatusDirty) {
      this._visible = this.checkVisibility();
      this._logicStatusDirty = false;
    }
    return this._visible;
  }

  checkVisibility(initialFieldResponsesWithPendingLogicChecks = null, rootObjectForLogicCheck = null) {
    // Create new array so we don't tamper with parent
    const fieldResponsesWithPendingLogicChecks = [...(initialFieldResponsesWithPendingLogicChecks || []), this];

    let fieldBlockResult = true;
    let fieldResult = true;
    if (this.fieldBlock) {
      // Field Blocks can also have their own logic groups that apply to all fields within them, similar to forms
      if (this.fieldBlock.logic_groups.length > 0) {
        if (!rootObjectForLogicCheck) {
          rootObjectForLogicCheck = this;
        }
        fieldBlockResult = this.fieldBlock.logic_groups.some(
          logicGroup => logicGroup.logicResultForRecapResponse(this.recapResponse, this.formResponse, fieldResponsesWithPendingLogicChecks, rootObjectForLogicCheck)
        );
      }

      // Compare location variables
      if (fieldBlockResult) {
        fieldBlockResult = this.checkLocationVariablesOnObject(this.fieldBlock);
      }

      // Compare role variables
      if (fieldBlockResult) {
        fieldBlockResult = this.checkRoleVariablesOnObject(this.fieldBlock);
      }

      // Compare locations
      if (fieldBlockResult) {
        fieldBlockResult = this.checkLocationsOnObject(this.fieldBlock);
      }

      // Compare location filters
      if (fieldBlockResult) {
        fieldBlockResult = this.checkLocationFiltersOnObject(this.fieldBlock);
      }
    }

    // If the field block is visible, check everything on the field instance
    if (fieldBlockResult) {
      if (this.fieldInstance.logic_groups.length > 0) {
        if (!rootObjectForLogicCheck) {
          rootObjectForLogicCheck = this;
        }
        fieldResult = this.fieldInstance.logic_groups.some(
          logicGroup => logicGroup.logicResultForRecapResponse(this.recapResponse, this.formResponse, fieldResponsesWithPendingLogicChecks, rootObjectForLogicCheck)
        );
      }

      // Compare location variables
      if (fieldResult) {
        fieldResult = this.checkLocationVariablesOnObject(this.fieldInstance);
      }

      // Compare role variables
      if (fieldResult) {
        fieldResult = this.checkRoleVariablesOnObject(this.fieldInstance);
      }

      // Compare locations
      if (fieldResult) {
        fieldResult = this.checkLocationsOnObject(this.fieldInstance);
      }

      // Compare location filters
      if (fieldResult) {
        fieldResult = this.checkLocationFiltersOnObject(this.fieldInstance);
      }
    }

    return fieldBlockResult && fieldResult;
  }

  checkLocationVariablesOnObject(fieldInstanceOrFieldBlock) {
    const objectLocationVariables = fieldInstanceOrFieldBlock.location_variables;
    const eventLocationVariables = this.recapResponse.eventLocationVariables;
    if (objectLocationVariables.length) {
      const fieldLVIDs = objectLocationVariables.map(lv => parseInt(lv.id));
      const eventLVIDs = eventLocationVariables.map(lv => parseInt(lv.id));
      return fieldLVIDs.some(id => eventLVIDs.indexOf(id) !== -1);
    }
    return true;
  }

  checkRoleVariablesOnObject(fieldInstanceOrFieldBlock) {
    const objectRoleVariables = fieldInstanceOrFieldBlock.role_variables;
    if (objectRoleVariables.length) {
      const objectRoleVariableIDs = objectRoleVariables.map(roleVariable => parseInt(roleVariable.role_variables_id));
      // Numbers on both sides, as the form-level check compares them: user_role_id arrives as a string
      // (the API sends it as one, and the preview binds it as an attribute), and indexOf is strict.
      return objectRoleVariableIDs.indexOf(parseInt(this.user_role_id)) > -1;
    }
    return true;
  }

  checkLocationsOnObject(fieldInstanceOrFieldBlock) {
    const objectLocations = fieldInstanceOrFieldBlock.locations;
    if (objectLocations.length) {
      const objectLocationIDs = objectLocations.map(loc => parseInt(loc.location_id));
      const locationId = this.current_location && parseInt(this.current_location.id) || null;
      return locationId ? objectLocationIDs.indexOf(+locationId) > -1 : true;
    }
    return true;
  }

  checkLocationFiltersOnObject(fieldInstanceOrFieldBlock) {
    const logicGroups = fieldInstanceOrFieldBlock.logic_groups;
    if (logicGroups.length) {
      const results = [];
      logicGroups.forEach(group =>
        group.filters.forEach(filter => {
          const location = this.current_location;
          if (location) {
            const isNumber = filter.type === 'zip' || filter.type === 'chain' || filter.type === 'market'
            let compareValue = null;
            if (filter.type === 'zip' || filter.type === 'city' || filter.type === 'state' || filter.type === 'market') {
              compareValue = location[filter.type];
            } else if (filter.type === 'chain') {
              if (filter.subtype && filter.subtype === 'child chain') {
                compareValue = location['chains_id'];
              } else {
                compareValue = location['parent_chains_id'];
              }
            }
            if (compareValue) {
              if (isNumber) {
                results.push(+compareValue === +filter.value);
              } else {
                results.push(compareValue === filter.value);
              }
            } else {
              results.push(false);
            }
          } else {
            results.push(false);
          }
        })
      )
      return results.every(result => !!result);
    }
    return true;
  }

  checkLogicModifiers() {
    this.fieldInstance.logic_groups.forEach(group =>
      group.modifiers.forEach(modifier =>
        modifier.logicModificationResultForRecapResponse(this.recapResponse, this)
      )
    );
  }

  collectValues() {
      this.storeCurrentValue();
      return this;
  }

  validate() {
    // Bypass validation checks for read-only fields (MOSAICPT-1882)
    if (!this.fieldType || this.fieldType.isReadOnly) {
      this.errors = [];
      return this.errors;
    }

    let validationErrors = this.fieldInstance.validations
      .map(validation => validation.validateResponse(this, this.formResponse))
      .reduce((pre, cur) => pre.concat(cur), []);
    let fieldTypeErrors = this.fieldInstance.field.fieldType.validateResponse(this) || [];
    this.errors = validationErrors.concat(fieldTypeErrors);
    if (!this.errors.length) {
      this.storeCurrentValue();
    } else {
      console.warn("FieldResponse.validate() got errors:", this.errors);
    }
    return this.errors;
  }

  updateDisplayLogic(changedFieldResponses) {
    this.setLogicStatusDirtyIfAffected(changedFieldResponses);
    return this.visible;
  }

  storeCurrentValue() {
    this.value = this.currentValue;
  }

  get documentsToUpload() {
    return this._documentsToUpload;
  }

  addDocumentToUpload(document) {
    this.documentsToUpload.push(document);
    this.setLogicStatusDirty();
  }

  removeDocumentToUpload(document) {
    this._documentsToUpload = this.documentsToUpload.filter(currDoc => currDoc.id !== document.id);
    this.setLogicStatusDirty();
  }

  clearDocumentsToUpload() {
    this._documentsToUpload = [];
    this.setLogicStatusDirty();
  }

  documentWasUploaded(document, newDocumentId) {
    this.removeDocumentToUpload(document);
    if (this.fieldInstance.field.fieldType.isMultipleFileUpload) {
      // Append to value
      if (this.value && this.value.indexOf('null') === -1) {
        // MJOL-1826 Multi image delimiter should be | not ,
        this.value += '|' + newDocumentId;
      } else {
        this.value = newDocumentId;
      }
    } else {
      // Replace value
      this.value = newDocumentId;
    }
    this.setLogicStatusDirty();
  }

  get documentsToDelete() {
    return this._documentsToDelete;
  }

  addDocumentToDelete(document) {
    this.documentsToDelete.push(document);
    this.setLogicStatusDirty();
  }

  removeDocumentToDelete(document) {
    this._documentsToDelete = this.documentsToDelete.filter(currDoc => currDoc.id !== document.id);
    this.setLogicStatusDirty();
  }

  clearDocumentsToDelete() {
    this._documentsToDelete = [];
    this.setLogicStatusDirty();
  }
}

export default FieldResponse;
